/**
 * The gamepad -- the third driver of the input struct.
 *
 * The architecture hands this file its whole design (docs/ARCH-PLAN.md
 * §4.1, WS-16): player.update(dt, input, camYaw) reads the same ten-field
 * struct whoever fills it, and the server's brain already proves any
 * writer gets identical physics -- ai/steer.js is a pair of hands on WASD,
 * and this file is a pair of thumbs on the same keys.  Nothing here
 * computes movement; it writes magnitudes into the struct and lets the
 * engine do what it does for the keyboard, the touch stick and the AI.
 *
 * The camera-space contract, and why there is no new convention here:
 * steer.js proves (measured, and pinned by ai.test.mjs) that `forward`
 * walks along (-sin camYaw, -cos camYaw) -- the AI steers by SETTING
 * camYaw and holding W.  A human's camYaw is not free for that trick (the
 * right stick owns it), but the identical algebra runs the other way
 * round: the left stick's deflection is already a camera-space vector
 * (stick up = away from the camera), so it decomposes into the four
 * magnitude fields exactly as touch.js's driveStick does, and player.js's
 * rotation of (mf, mr) by the real camYaw lands the cat on the stick's
 * bearing.  One convention, three drivers; the unit tests
 * (packages/client/test/gamepad.test.mjs) pin the composed identity
 * against a real Player the same way the steering test does.  The plan's
 * "analog->digital" phrasing predates WS-06 writing the struct contract
 * down: the direction fields are MAGNITUDES, player.js's axis() was built
 * for "a stick's 0.42", so the pad writes analog values and half a stick
 * is a creep -- the same choice the touch stick made, for the same reason.
 *
 * Polled, not evented, because that is the Gamepad API: buttons have no
 * events, so the driver is a frame-loop step, registered at the TAIL of
 * loop.js's STEPS -- the list is append-only by design, precisely so this
 * lands without touching another workstream's line.  Tail position costs
 * one frame: a press sampled this frame is consumed by next frame's
 * `inputs` and `player` steps, the same worst-case a between-frames
 * keydown already has.  The jump rides main's held/pressed latch and the
 * sit and swipe pulses ride the same one-frame writes the keyboard uses,
 * so every edge-vs-level subtlety the typedef documents is inherited
 * here, not re-solved.
 *
 * INERT WHEN ABSENT is the load-bearing promise.  With no pad ever
 * connected this step is one integer compare per frame and writes nothing
 * -- keyboard and touch behaviour are byte-identical to a build without
 * this file, and ring 3 (scripts/gates/browser.mjs), which runs padless,
 * holds exactly that.  Connection is tracked by the connect/disconnect
 * events alone, so the absent path never even calls getGamepads().  While
 * a pad IS connected, the struct is written only while the pad is
 * actually asking for something -- deflection past the dead zone, a held
 * level, a press edge -- and released exactly once when it stops, the
 * discipline touch.js's releaseStick keeps, so a parked pad never fights
 * the keyboard for the struct.
 *
 * No remapping UI, on purpose: on the Deck, Steam Input owns that layer
 * (§4.1), and everything Steam Input emits arrives here wearing the
 * standard mapping this file reads.
 */

/**
 * The left stick's radial dead zone.
 *
 * Twice the touch stick's 0.10, because a physical stick is worse than a
 * thumb on glass: a worn pot rests off-centre by 0.05-0.15 (XInput's own
 * per-axis constant is 0.24), and without a dead zone a cat set down on a
 * shelf walks away on its own.  Radial, not per-axis -- a square zone
 * makes diagonals stickier than cardinals -- and deflection past the zone
 * rescales to the full 0..1 (touch.js's arithmetic, kept exactly), so the
 * zone costs reach rather than resolution and the cat can still hit its
 * own top speed.
 */
export const DEAD = 0.18;

/**
 * Where the stick starts asking for a run -- touch.js's thresholds,
 * copied, not imported: they are that file's private tuning for the same
 * problem (a proportional stick meeting a binary gait key), and a value
 * imported from a sibling driver would move both when either was re-tuned
 * for its own hardware.  The hysteresis gap stops a stick held exactly on
 * the line flapping between gaits several times a second.
 */
export const SPRINT_ON = 0.86;
export const SPRINT_OFF = 0.76;

/**
 * Right-stick look rates, radians per second at full deflection.
 *
 * The mouse and thumb constants are per-PIXEL and do not transplant; a
 * stick holds a rate instead.  Full tilt sweeps a half turn in about a
 * second -- brisk enough to answer the dog (2.6 m/s and gaining), calm
 * enough to frame a screenshot -- and the response is squared on the
 * radius (lookVector below), so half tilt is a quarter rate for fine
 * framing.  Pitch keeps the mouse's sign: stick down looks down, the
 * direction every other look in the game already goes.  Tuned by
 * arithmetic against the mouse's numbers, NOT by a hand on a real stick
 * -- honest status: a Deck session may want these touched.
 */
const YAW_RATE = 3.2;
const PITCH_RATE = 1.8;

/** The pitch clamp, and the dolly clamp under it -- the same numbers the
    mouse, wheel and pinch paths hold, and deliberately not a shared
    helper: context.js already says the clamp is the one thing the look
    paths must agree on, and it reads better duplicated than
    parameterised. */
const PITCH_MIN = -0.30;
const PITCH_MAX = 1.02;
const DIST_MIN = 0.9;
const DIST_MAX = 5.0;
/** Metres of camera dolly per held second of d-pad -- spans the clamp in
    about two seconds, the pace two comfortable pinches set. */
const DOLLY_RATE = 2.2;

/**
 * The buttons, by standard-mapping index (w3c.github.io/gamepad,
 * "Remapping").  The verbs are the plan's jump / sit / meow plus the rest
 * of the cat's keyboard, placed by the same logic touch.js stacks its
 * buttons: the most-used verb sits under the resting thumb.
 *
 *   A (0)  jump  -- through main's held/pressed latch: hold gathers a
 *                   pounce, release launches, a tap is a hop, exactly Space.
 *   B (1)  sit   -- one-frame pulse, exactly C.
 *   X (2)  meow  -- one-shot through the touch layer's hook (presentation,
 *                   not simulation; player.js never hears it), exactly V.
 *   Y (3)  cat builder -- toggle, exactly B; it is also the one button
 *                   still read while a panel owns the screen, so the pad
 *                   can close what it opened.
 *   LB/LT (4,6) stalk -- level, either shoulder, exactly Ctrl.
 *   RB (5) swipe -- one-shot pulse, exactly F.
 *   RT (7) claw  -- level; held in the air against a climbable face it
 *                   hooks and clings, exactly the left mouse button.
 *   d-pad up/down (12,13) camera dolly -- the wheel a pad does not have.
 *
 * Start/select, the stick clicks and d-pad left/right stay unbound --
 * spare keys for verbs that do not exist yet beat a mapping nobody can
 * change (no remapping UI, see the header).
 */
const BTN_JUMP = 0;
const BTN_SIT = 1;
const BTN_MEOW = 2;
const BTN_BUILD = 3;
const BTN_STALK_A = 4;
const BTN_SWIPE = 5;
const BTN_STALK_B = 6;
const BTN_CLAW = 7;
const BTN_DOLLY_IN = 12;
const BTN_DOLLY_OUT = 13;

// ---------------------------------------------------------------------------
// the pure mapping -- importable under plain Node, tested without a browser
// ---------------------------------------------------------------------------

/**
 * Left-stick deflection -> the four direction magnitudes.
 *
 * touch.js's driveStick arithmetic with the pad's dead zone: clamp the
 * radius to 1 (a worn pot can report 1.02 on a diagonal), rescale past
 * the zone so the whole remaining sweep is usable, decompose along the
 * unit direction.  Stick up is away from the camera, which is what
 * `forward` means; the rotation into the world happens in player.js,
 * against the real camYaw, and nowhere else.
 *
 * @param {number} sx  left stick X, standard mapping: right is positive
 * @param {number} sy  left stick Y, standard mapping: DOWN is positive
 * @returns {{forward: number, back: number, left: number, right: number,
 *            m: number}}  the four struct fields, plus the post-dead-zone
 *            magnitude the sprint hysteresis reads
 */
export function moveVector(sx, sy) {
  const len = Math.hypot(sx, sy);
  const capped = Math.min(len, 1);
  const m = capped <= DEAD ? 0 : (capped - DEAD) / (1 - DEAD);
  const ux = len > 0 ? sx / len : 0;
  const uy = len > 0 ? sy / len : 0;
  const nx = ux * m;
  const ny = uy * m;
  return {
    forward: Math.max(0, -ny),
    back: Math.max(0, ny),
    right: Math.max(0, nx),
    left: Math.max(0, -nx),
    m,
  };
}

/**
 * One tick of the sprint hysteresis -- touch.js's flip condition as a
 * pure next-state function, so a table test can walk it.
 * @param {number} m     post-dead-zone magnitude, moveVector's `m`
 * @param {boolean} was  sprinting last frame
 * @returns {boolean}
 */
export function sprintNext(m, was) {
  return (was ? m < SPRINT_OFF : m > SPRINT_ON) ? !was : was;
}

/**
 * Right-stick deflection -> a look vector in [-1, 1] per axis.
 *
 * Same radial dead zone and rescale as the move stick, then the radius is
 * SQUARED: rate response, not position, wants a soft centre -- half tilt
 * giving a quarter rate is what makes small framing corrections possible
 * on a spring-centred stick.  Direction is preserved exactly (the curve
 * is applied to the radius, never per-axis, which would bend diagonals).
 *
 * @param {number} rx  right stick X, right positive
 * @param {number} ry  right stick Y, down positive
 * @returns {{x: number, y: number}}
 */
export function lookVector(rx, ry) {
  const len = Math.hypot(rx, ry);
  const capped = Math.min(len, 1);
  if (capped <= DEAD) return { x: 0, y: 0 };
  const m = (capped - DEAD) / (1 - DEAD);
  const c = m * m;
  return { x: (rx / len) * c, y: (ry / len) * c };
}

/** @param {number} v  @param {number} lo  @param {number} hi */
function clamp(v, lo, hi) {
  return v < lo ? lo : v > hi ? hi : v;
}

// ---------------------------------------------------------------------------
// the driver -- connection bookkeeping and the per-frame poll
// ---------------------------------------------------------------------------

/**
 * What the pad is currently holding, so every write has a matching
 * release.  One module-scope instance, like the keyboard's latches: there
 * is one input struct, so a second pad driving it would only arm-wrestle
 * the first -- firstPad() below reads the lowest connected slot and the
 * rest are ignored.
 */
const pad = {
  wired: false,      // listeners registered (lazily, first frame, browser only)
  count: 0,          // connected pads, by event bookkeeping -- the absent gate
  moving: false,     // we hold the four direction fields + sprint
  sprinting: false,
  jumping: false,    // we hold main's jumpHeld latch
  stalk: 0,          // the level we last wrote to raw.stalk
  claw: 0,           // the level we last wrote to input.claw
  prev: /** @type {boolean[]} */ ([]),   // last frame's pressed, by index
};

/** Count pads the honest way on every connect/disconnect -- the events
    carry the pad but not the census, and a stale count in either
    direction is either a dead driver or a per-frame poll for nobody. */
function recount() {
  try {
    let n = 0;
    for (const p of navigator.getGamepads()) if (p && p.connected) n += 1;
    pad.count = n;
  } catch {
    pad.count = 0;               // a permissions-policy refusal is "no pads"
  }
}

/** Register the connection listeners, once, from the first frame -- not at
    module load, so importing this file under plain Node (the unit tests)
    touches no browser API and has no side effect at all. */
function wire() {
  pad.wired = true;
  if (typeof navigator === "undefined" ||
      typeof navigator.getGamepads !== "function" ||
      typeof addEventListener !== "function") {
    return;                      // no Gamepad API: stay inert forever
  }
  addEventListener("gamepadconnected", recount);
  addEventListener("gamepaddisconnected", recount);
  recount();                     // a pad some browsers surface without an event
}

/** The lowest connected pad, or null.  Chromium keeps disconnected slots
    as nulls in the array, and the count above can outrun the array by a
    frame either way -- so trust only what is actually here. */
function firstPad() {
  try {
    for (const p of navigator.getGamepads()) if (p && p.connected) return p;
  } catch {
    // recount() will zero the gate on the next event; this frame has no pad
  }
  return null;
}

/** Is the pad holding anything that needs releasing? */
function engaged() {
  return pad.moving || pad.jumping || !!pad.stalk || !!pad.claw;
}

/** Let go of the stick: the four magnitudes, the gait, our own hysteresis
    -- touch.js's releaseStick, field for field.  @param {any} g */
function releaseStick(g) {
  g.input.forward = g.input.back = g.input.left = g.input.right = 0;
  g.input.sprint = 0;
  pad.sprinting = false;
  pad.moving = false;
}

/**
 * Let go of everything -- a panel took the screen, or the pad was
 * unplugged mid-gesture.  Everything the driver can HOLD is released;
 * the pulses (sit, swipe, meow) owe nothing, touch.js's releaseAll
 * bargain.  Releasing the jump latch is a falling edge, so a gathered
 * pounce launches -- exactly what the touch layer's jumpUp does when a
 * panel opens under a charging thumb.
 * @param {any} g
 */
function release(g) {
  if (pad.moving) releaseStick(g);
  if (pad.claw) { pad.claw = 0; g.input.claw = 0; }
  if (pad.stalk) { pad.stalk = 0; g.raw.stalk = 0; }
  if (pad.jumping) { pad.jumping = false; g.jumpHeld = 0; }
}

/** Remember this frame's pressed set for the next frame's edges.
    @param {readonly GamepadButton[]} bs */
function remember(bs) {
  pad.prev.length = bs.length;
  for (let i = 0; i < bs.length; i++) pad.prev[i] = !!(bs[i] && bs[i].pressed);
}

/**
 * The frame step.  One line in loop.js's STEPS registers it; everything
 * else about the pad lives here.
 */
export const gamepadStep = {
  name: "gamepad",
  /**
   * @param {any} g  the app/context.js `game` object -- typed away for the
   *                 same reason every step's is: its shape is main's
   *                 Object.assign union, which is exactly the debt the
   *                 typecheck ratchet holds at its ceiling
   * @param {{dt: number}} f
   */
  run(g, f) {
    if (!pad.wired) wire();
    if (pad.count === 0) {
      // The absent path, and the whole of it.  `engaged` survives a
      // surprise unplug: the disconnect event zeroes the count before the
      // poll can see the pad go, so the release happens here.
      if (engaged()) release(g);
      return;
    }
    const p = firstPad();
    if (!p) {
      if (engaged()) release(g);
      return;
    }

    const bs = p.buttons;
    /** @type {(i: number) => boolean} */
    const held = (i) => !!(bs[i] && bs[i].pressed);
    /** @type {(i: number) => boolean} */
    const edge = (i) => held(i) && !pad.prev[i];

    // Panels own the screen, the same bargain the frame loop strikes for
    // touch: let go rather than leave a stick's worth of input latched
    // under a dialog.  Y stays live so the pad can close the builder it
    // opened; the edges are still remembered, so a button held across the
    // panel does not fire a stale press the frame it lifts.
    if (g.touch?.hooks.blocked?.()) {
      if (engaged()) release(g);
      if (edge(BTN_BUILD)) g.customizer?.toggle();
      remember(bs);
      return;
    }

    const mv = moveVector(p.axes[0] ?? 0, p.axes[1] ?? 0);

    // The start gate: the first thing the pad does also starts the game,
    // the way the first keydown and the first touch do.  Through the touch
    // layer's begin hook -- one begin(), three drivers -- and only until
    // it lands: repeated calls buy nothing here because a pad press is not
    // a user gesture, so the audio contexts wake on the player's first
    // real click, key or touch regardless (a platform rule, not ours).
    if (!g.started && (mv.m > 0 || bs.some((b) => b && b.pressed))) {
      g.touch?.hooks.begin?.();
    }

    // Left stick -> the struct, only while deflected; released ONCE when
    // it centres, then the keyboard owns the fields again.
    if (mv.m > 0) {
      g.input.forward = mv.forward;
      g.input.back = mv.back;
      g.input.left = mv.left;
      g.input.right = mv.right;
      pad.sprinting = sprintNext(mv.m, pad.sprinting);
      g.input.sprint = pad.sprinting ? 1 : 0;
      pad.moving = true;
    } else if (pad.moving) {
      releaseStick(g);
    }

    // Right stick -> the camera, as a rate.  The clamp is the mouse's and
    // the thumb's, duplicated on purpose (see the constants above).
    const lv = lookVector(p.axes[2] ?? 0, p.axes[3] ?? 0);
    if (lv.x !== 0 || lv.y !== 0) {
      g.camYaw -= lv.x * YAW_RATE * f.dt;
      g.camPitch = clamp(g.camPitch + lv.y * PITCH_RATE * f.dt, PITCH_MIN, PITCH_MAX);
    }
    if (held(BTN_DOLLY_IN) !== held(BTN_DOLLY_OUT)) {
      const dir = held(BTN_DOLLY_IN) ? -1 : 1;
      g.camDist = clamp(g.camDist + dir * DOLLY_RATE * f.dt, DIST_MIN, DIST_MAX);
    }

    // Jump, through main's latch: the press edge arms held+pressed so a
    // tap shorter than a frame still launches at power 1.0, and the
    // release is the falling edge player.js fires on.  Space and the
    // touch jump button write these exact fields.
    if (edge(BTN_JUMP)) {
      g.jumpHeld = 1;
      g.jumpPressed = 1;
      pad.jumping = true;
    } else if (pad.jumping && !held(BTN_JUMP)) {
      g.jumpHeld = 0;
      pad.jumping = false;
    }

    if (edge(BTN_SIT)) g.input.sitToggle = 1;      // pulse; the player step zeroes it
    if (edge(BTN_MEOW)) g.touch?.hooks.meow?.();   // presentation, never the struct
    if (edge(BTN_BUILD)) g.customizer?.toggle();
    if (edge(BTN_SWIPE)) g.raw.swipe = 1;          // pulse; gated on the cat's clips

    // The levels, written on change only -- a held shoulder is one write,
    // not sixty a second fighting whatever Ctrl is doing.
    const stalk = held(BTN_STALK_A) || held(BTN_STALK_B) ? 1 : 0;
    if (stalk !== pad.stalk) { pad.stalk = stalk; g.raw.stalk = stalk; }
    const claw = held(BTN_CLAW) ? 1 : 0;
    if (claw !== pad.claw) { pad.claw = claw; g.input.claw = claw; }

    remember(bs);
  },
};
